/**
 * Export service manager - coordinates all exporters
 */

import type { DocumentExporter, ExportFormat, ExportOptions, ExportResult } from './types.js';
import { DocxExporter } from './DocxExporter.js';

export interface ExportServiceConfig {
  /** Get the auth token for authenticated exports */
  getAuthToken?: () => string | null;
}

export class ExportService {
  private exporters: Map<ExportFormat, DocumentExporter>;
  private config: ExportServiceConfig;

  constructor(config: ExportServiceConfig = {}) {
    this.config = config;
    this.exporters = new Map();
    this.registerDefaultExporters();
  }

  /**
   * Register default exporters
   */
  private registerDefaultExporters(): void {
    this.exporters.set('docx', new DocxExporter(this.config.getAuthToken));
  }

  /**
   * Register a custom exporter
   */
  registerExporter(exporter: DocumentExporter): void {
    this.exporters.set(exporter.getFormat(), exporter);
  }

  /**
   * Get an exporter for a specific format
   */
  getExporter(format: ExportFormat): DocumentExporter | undefined {
    return this.exporters.get(format);
  }

  /**
   * Check if an exporter is available for a format
   */
  isFormatAvailable(format: ExportFormat): boolean {
    const exporter = this.exporters.get(format);
    return exporter ? exporter.isAvailable() : false;
  }

  /**
   * Export markdown content to the specified format
   */
  async export(
    markdown: string,
    format: ExportFormat,
    options?: ExportOptions
  ): Promise<ExportResult> {
    const exporter = this.exporters.get(format);

    if (!exporter) {
      return {
        success: false,
        error: `No exporter registered for format: ${format}`,
      };
    }

    if (!exporter.isAvailable()) {
      return {
        success: false,
        error: `Exporter for ${format} is not available.`,
      };
    }

    return await exporter.export(markdown, options);
  }

  /**
   * Download an exported file
   */
  downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export and download in one step
   */
  async exportAndDownload(
    markdown: string,
    format: ExportFormat,
    options?: ExportOptions
  ): Promise<boolean> {
    const result = await this.export(markdown, format, options);

    if (!result.success || !result.blob || !result.filename) {
      if (result.error) {
        console.error('Export error:', result.error);
        alert(`Export failed: ${result.error}`);
      }
      return false;
    }

    this.downloadBlob(result.blob, result.filename);
    return true;
  }

  /**
   * Get list of available export formats
   */
  getAvailableFormats(): ExportFormat[] {
    return Array.from(this.exporters.keys()).filter((format) =>
      this.isFormatAvailable(format)
    );
  }
}
